import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import { getComparison } from "../../../services/Dashboard/Table";
import QueryPanel from "./Comparison/QueryPanel";
import PerformanceTable from "./Comparison/PerformanceTable";

const ComparisonTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const isDateRange = useMemo(() => Boolean(startDate && endDate), [startDate, endDate]);
  const showChips = Boolean(startDate || endDate);
  const isSearchDisabled = loading || !startDate;

  const fetchComparison = useCallback(async (overrideFilters = null) => {
    setLoading(true);
    setHasSearched(true);
    try {
      let filters = {};
      if (!overrideFilters) {
        if (startDate && !endDate) {
          filters.date = dayjs(startDate).format("YYYY-MM-DD");
        } else if (startDate && endDate) {
          filters.from_date = dayjs(startDate).format("YYYY-MM-DD");
          filters.to_date = dayjs(endDate).format("YYYY-MM-DD");
        }
      } else if (overrideFilters !== true) {
        filters = overrideFilters;
      }
      const response = await getComparison(filters);
      setData(response.results || []);
    } catch (error) {
      console.error("Error fetching comparison data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Initial load
  useEffect(() => {
    fetchComparison(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = useCallback(() => {
    fetchComparison();
  }, [fetchComparison]);

  const handleClearDates = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setHasSearched(false);
    fetchComparison({});
  }, [fetchComparison]);

  // Derived: rate accessors
  const getWinRateValue = useCallback(
    (row) => (isDateRange ? row.avg_win_rate : row.strike_win_rate),
    [isDateRange]
  );

  const getPlaceRateValue = useCallback(
    (row) => (isDateRange ? row.avg_place_rate : row.strike_place_rate),
    [isDateRange]
  );

  // Derived: best win rate for "Top Strike" highlighting
  const bestWinRate = useMemo(() => {
    return data.reduce((max, row) => {
      const value = getWinRateValue(row);
      if (value === null || value === undefined || isNaN(value)) return max;
      return Math.max(max, Number(value));
    }, Number.NEGATIVE_INFINITY);
  }, [data, getWinRateValue]);

  // Derived: active status badge
  const activeState = useMemo(() => {
    if (loading) {
      return {
        label: "Syncing",
        color: "var(--color-primary)",
        background: "rgba(200, 255, 0, 0.12)",
        border: "1px solid rgba(200, 255, 0, 0.25)",
      };
    }
    if (isDateRange) {
      return {
        label: "Range Scan",
        color: "var(--color-accent-amber)",
        background: "rgba(255, 168, 0, 0.12)",
        border: "1px solid rgba(255, 168, 0, 0.25)",
      };
    }
    if (startDate) {
      return {
        label: "Single-Day Scan",
        color: "var(--color-text)",
        background: "var(--color-text-light)",
        border: "1px solid rgba(143, 149, 162, 0.25)",
      };
    }
    return {
      label: "Awaiting Input",
      color: "var(--color-text-muted)",
      background: "var(--color-text-light)",
      border: "1px solid rgba(143, 149, 162, 0.25)",
    };
  }, [loading, isDateRange, startDate]);

  // Derived: descriptive copy for current filter state
  const performanceCopy = useMemo(() => {
    if (startDate && endDate) return "Showing performance data for the selected date range";
    if (startDate) return "Showing performance data for the selected venue session";
    return "Select a date or range to scan model performance telemetry";
  }, [startDate, endDate]);

  return (
    <Box sx={{ display: "grid", gap: 3, color: "var(--color-text)" }}>
      <QueryPanel
        startDate={startDate}
        endDate={endDate}
        loading={loading}
        activeState={activeState}
        showChips={showChips}
        isSearchDisabled={isSearchDisabled}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onSearch={handleSearch}
        onClearDates={handleClearDates}
      />

      <PerformanceTable
        data={data}
        loading={loading}
        hasSearched={hasSearched}
        isDateRange={isDateRange}
        activeState={activeState}
        performanceCopy={performanceCopy}
        bestWinRate={bestWinRate}
        getWinRateValue={getWinRateValue}
        getPlaceRateValue={getPlaceRateValue}
      />
    </Box>
  );
};

export default ComparisonTable;
